
import { useEffect } from 'react';
import { useAuth } from '@/contexts/auth';
import { usePollFetch } from './polls/usePollFetch';
import { usePollDialog } from './polls/usePollDialog';
import { usePollActions } from './polls/usePollActions';
import { usePollVoting } from './polls/usePollVoting';

export const usePollManagement = () => {
  const { polls, setPolls, isLoading, refetchPolls } = usePollFetch({
    includeAllStatuses: true, // Include all poll statuses, not just live ones
    limit: 25 // Optimize performance with pagination
  });
  
  const {
    dialogOpen,
    setDialogOpen,
    currentPoll,
    setCurrentPoll,
    isEditing,
    handleOpenDialog
  } = usePollDialog(polls);
  
  const {
    handleSavePoll,
    handleDeletePoll,
    handleActivatePoll,
    handleClosePoll,
    handleReopenPoll,
    linkPollToCard
  } = usePollActions(polls, setPolls, setDialogOpen);
  
  const { handleVotePoll } = usePollVoting(polls, setPolls);

  // Process polls to ensure they have total_votes
  useEffect(() => {
    if (polls.length > 0) {
      const updatedPolls = polls.map(poll => {
        // If poll doesn't have total_votes, calculate it from options
        if (poll.total_votes === undefined) {
          // Ensure options exists and is an array before calling reduce
          const totalVotes = (poll.options && Array.isArray(poll.options)) 
            ? poll.options.reduce((sum, option) => sum + (option.votes || 0), 0)
            : 0;
          return { ...poll, total_votes: totalVotes };
        }
        return poll;
      });
      
      // Only update if something changed
      const needsUpdate = updatedPolls.some((poll, idx) => 
        poll.total_votes !== polls[idx].total_votes
      );
      
      if (needsUpdate) {
        setPolls(updatedPolls);
      }
    }
  }, [polls, setPolls]);

  // Wrap the handleSavePoll function to make it easier to use
  const wrappedHandleSavePoll = () => {
    handleSavePoll(currentPoll, isEditing);
  };

  // Trigger initial polls fetch on component mount only if polls are empty
  useEffect(() => {
    // Only fetch if polls are empty and we're not already loading
    if (polls.length === 0 && !isLoading) {
      console.log('UsePollManagement: No polls loaded, triggering initial fetch');
      // Add a small delay to ensure all dependencies are initialized
      const timer = setTimeout(() => {
        refetchPolls();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [polls.length, isLoading, refetchPolls]);

  return {
    polls,
    isLoading,
    dialogOpen,
    setDialogOpen,
    currentPoll,
    setCurrentPoll,
    isEditing,
    handleOpenDialog,
    handleSavePoll: wrappedHandleSavePoll,
    handleDeletePoll,
    handleActivatePoll,
    handleClosePoll,
    handleReopenPoll,
    handleVotePoll,
    linkPollToCard,
    refetchPolls
  };
};
